/**
 * Run now from a product's header (CU-869fm5juc): what the dialog, the
 * button, the reason next to it and the toast say.
 *
 * A Run now of product P launches every strategy on in P, whole, outside
 * their run hours, and none of another product (brief criterion 4). With
 * one pricelist, one product, P launched whole is what a scheduled pass
 * over P would do: no strategy of another product is pulled in. The
 * per-strategy Run now of CU-869fm5ju2 is gone (criterion 10).
 *
 * The figures are the engine's, precalculated in the estimate as
 * `per_product[pid].run_now` (plan D5): the front never sums them.
 * Without them the dialog still launches, without numbers (Curro,
 * 2026-10-23, plan D6).
 *
 * Every field read from the engine is named once, in `F` and `R` below.
 */
import { fmtInt } from './cadence.js'

/** The engine's field names — estimate side (`per_product[pid]`). */
const F = {
	perProduct: 'per_product',      // estimate.per_product
	preview: 'run_now',             // per_product[pid].run_now
	searches: 'searches',           // run_now.searches — the union's searches
	pricePoints: 'tasks',           // run_now.tasks — the union's price points
	offices: 'offices',             // run_now.offices — distinct offices of the union
	pickupDates: 'pickup_dates',    // run_now.pickup_dates — distinct pickup dates
	strategies: 'strategies',       // run_now.strategies[] — launched, by Prio then id
	id: 'strategy_id',
	name: 'name',
	priority: 'priority',
	strategyPricePoints: 'tasks',   // each strategy's own price points, shared ones included
}

/** The engine's field names — force report side. */
const R = {
	ran: 'ran',                     // false: refused (`reason` says why)
	launched: 'launched',           // false: nothing due / slot already ran (`reason` says why)
	why: 'reason',
	searches: 'composed',           // report.composed — searches launched
	pricePoints: 'tasks',           // report.tasks — price points launched
	strategies: 'strategies',       // report.strategies[]
}

export const SUBTITLE = 'Every strategy on in this product, outside their run hours'
export const BODY = 'Each price is set as a scheduled pass would set it — the lowest Prio wins. Executions will mark this pass by hand.'
export const NO_PREVIEW_DETAIL = 'The engine will say how many searches before it launches them. Searches are billed.'
export const PRICE_POINT_DEFINITION = 'A price point is one office × pickup date × rental length × site.'
export const PRICE_POINT_SHARED = 'Each strategy counts every price point it quotes, shared ones included; the total counts them once.'
export const NOTHING_ON = 'No strategy on — nothing to run'
export const PRODUCT_INVALID = 'The product is invalid — fix it in Setup → Products before running it'
export const OFF_REASON = 'off — switch it on to include it'
/** An enabled strategy the engine does not launch now (no pickup date today, no cadence). Copy pending UX (plan Dudas). */
export const NOTHING_TO_SEARCH = 'nothing to search now'

const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v))
const plural = (n, one, many) => (n === 1 ? one : many)
export const searchesLabel = (n) => `${fmtInt(n)} ${plural(n, 'search', 'searches')}`
export const pricePointsLabel = (n) => `${fmtInt(n)} ${plural(n, 'price point', 'price points')}`
export const strategiesLabel = (n) => `${n} ${plural(n, 'strategy', 'strategies')}`
export const titleOf = (name) => `Run "${name}" now?`

const byPrio = (a, b) => ((a.priority ?? Infinity) - (b.priority ?? Infinity)) || String(a.id).localeCompare(String(b.id))

/**
 * The Run now preview of product `pid` from the tenant's estimate, or
 * `null` — no figures, the dialog still launches (D6):
 *   - no `per_product` at all (engine older than CU-869fm5juc, down, or
 *     still counting);
 *   - `per_product` without `pid` (TL C2): webback caches the estimate
 *     60 s per worker, so a product switched on a moment ago may be
 *     missing — never read as "nothing to run". The real refusals are
 *     webback's 409 `nothing_enabled` and the engine's `launched: false`.
 * Otherwise `{ total, pricePoints, offices, pickupDates, launched }`,
 * `launched` = `[{ id, name, priority, pricePoints }]` by Prio then id.
 */
export function productRunNowPreview(shopsData, pid) {
	const rn = shopsData?.[F.perProduct]?.[String(pid)]?.[F.preview]
	if (!rn) return null
	const launched = (rn[F.strategies] || []).map(s => ({
		id: s[F.id],
		name: s[F.name] ?? s[F.id],
		priority: num(s[F.priority]),
		pricePoints: num(s[F.strategyPricePoints]),
	})).sort(byPrio)
	return {
		total: num(rn[F.searches]),
		pricePoints: num(rn[F.pricePoints]),
		offices: num(rn[F.offices]),
		pickupDates: num(rn[F.pickupDates]),
		launched,
	}
}

/** True only when the engine said the product launches no search (TL C2): then a toast, no dialog. */
export const launchesNothing = (preview) => preview?.total === 0

/**
 * `{ disabled, reason }` of a product's Run now. `strategies` are the
 * product's rows as the table decorates them (`enabled`, and `product`
 * — the server's verdict on the product). The reason is written next to
 * the button and in its title (UX Obs. 4).
 */
export function runNowState(strategies) {
	const list = Array.isArray(strategies) ? strategies : []
	if (list.some(s => s?.product && s.product.state && s.product.state !== 'ok')) {
		return { disabled: true, reason: PRODUCT_INVALID }
	}
	if (!list.some(s => s?.enabled)) return { disabled: true, reason: NOTHING_ON }
	return { disabled: false, reason: null }
}

/**
 * The product's strategies the pass does not launch, by Prio:
 * `[{ id, name, priority, reason }]`. Off (or never switched on) → "off —
 * switch it on to include it". On but not in the engine's list → "nothing
 * to search now". Without a preview only the ones off are known.
 */
export function notLaunched(strategies, preview) {
	const launched = new Set((preview?.launched || []).map(s => String(s.id)))
	return (Array.isArray(strategies) ? strategies : [])
		.filter(s => !s.enabled || (preview && !launched.has(String(s.id))))
		.map(s => ({ id: s.id, name: s.name, priority: num(s.priority), reason: s.enabled ? NOTHING_TO_SEARCH : OFF_REASON }))
		.sort(byPrio)
}

/** "N strateg(y|ies) off" when every one is off, "N strateg(y|ies)" otherwise — the "Not launched" heading. */
export function notLaunchedLabel(items) {
	const n = items.length
	return items.every(s => s.reason === OFF_REASON) ? `${strategiesLabel(n)} off` : strategiesLabel(n)
}

/** The confirm button: "Run now · N searches", or "Run now" without figures. */
export const confirmLabel = (preview) => (preview?.total != null ? `Run now · ${searchesLabel(preview.total)}` : 'Run now')

/** The success toast, from the engine's force report. */
export function launchedToast(name, report) {
	const searches = num(report?.[R.searches])
	const pricePoints = num(report?.[R.pricePoints])
	const k = (report?.[R.strategies] || []).length
	const who = k ? ` (${strategiesLabel(k)})` : ''
	if (searches == null) return `Pass of "${name}" launched${who}.`
	const points = pricePoints != null ? ` · ${pricePointsLabel(pricePoints)}` : ''
	return `Launched ${searchesLabel(searches)}${points} for "${name}"${who}.`
}

/**
 * `{ ok, text }` for the toast after the engine answered. A report with
 * `ran: false` or `launched: false` launched nothing: never "launched",
 * it says what the engine said (`reason`).
 */
export function launchOutcome(name, report) {
	if (report?.[R.ran] === false || report?.[R.launched] === false) {
		const why = report?.[R.why]
		return { ok: false, text: `Nothing launched for "${name}"${why ? `: ${why}` : ''}.` }
	}
	return { ok: true, text: launchedToast(name, report) }
}
